import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { alertDetailsModalTypes } from '../../../../utility/alertDetailsModalTypes';
import AcknowledgeAlertModalContent from './AcknowledgeAlertModal/AcknowledgeAlertModalContent/AcknowledgeAlertModalContent';
import ModalComponent from '../../../layout/Modals/ModalComponent/ModalComponent';
import { connectModalContainerContextToComponent } from '../../../../contexts/ModalContext/ModalContext';
import GenericErrorModal from './GenericErrorModal/GenericErrorModal';
import {
  fetchSingleAlert,
  selectSingleAlertCrudOperation,
  selectSingleAlertCrudStatus,
  selectSingleAlertErrors,
} from '../../../../redux/slices/singleAlertSlice/singleAlertSlice';
import { CrudType } from '../../../../utility/types/CrudType';
import { CrudStatus } from '../../../../utility/types/CrudStatus';

const AlertDetailsModal = (props) => {
  const {
    handleDeleteAlertAction,
    handleDeleteAlert,
    handleDeleteAlertComment,
    handleAcknowledgeAlert,
    type,
    modalPayload,
    description,
    setModalJSX,
    alertId,
  } = props;

  const [deleteCommentButtonText, setDeleteCommentButtonText] = useState('Delete Comment');
  const [deleteCommentSubmitting, setDeleteCommentSubmitting] = useState(false);
  const [deleteAlertButtonText, setDeleteAlertButtonText] = useState('Delete Alert');
  const [deleteAlertSubmitting, setDeleteAlertSubmitting] = useState(false);
  const [deleteActionButtonText, setDeleteActionButtonText] = useState('Delete Action');
  const [deleteActionSubmitting, setDeleteActionSubmitting] = useState(false);
  const [acknowledgeAlertButtonText, setAcknowledgeAlertButtonText] = useState('Acknowledge Alert');
  const [acknowledgeAlertSubmitting, setAcknowledgeAlertSubmitting] = useState(false);
  const [acknowledgeText, setAcknowledgeText] = useState('');
  const [show, setShow] = useState(true);

  const dispatch = useDispatch();
  const crudOperation = useSelector(selectSingleAlertCrudOperation);
  const crudStatus = useSelector(selectSingleAlertCrudStatus);
  const errorList = useSelector(selectSingleAlertErrors);

  const closeModalHandler = () => {
    setShow(false);
    setModalJSX((<></>));
  };

  const handleDeleteSuccess = () => {
    setDeleteAlertButtonText('Delete Alert');
    setDeleteAlertSubmitting(false);
    closeModalHandler();
    window.location.assign('/s/alerts');
  };

  const handleDeleteError = () => {
    setModalJSX(
      <GenericErrorModal
        show={show}
        message={errorList.message}
      />,
    );
  };

  React.useEffect(() => {
    if (crudOperation === CrudType.DELETE) {
      switch (crudStatus) {
        case CrudStatus.SUCCESS:
          handleDeleteSuccess();
          break;
        case CrudStatus.ERROR:
          handleDeleteError();
          break;
        default:
          break;
      }
    }
  }, [crudOperation, crudStatus]);

  const onConfirmDeleteComment = () => {
    setDeleteCommentButtonText('Deleting Comment...');
    setDeleteCommentSubmitting(true);
    handleDeleteAlertComment(modalPayload)
      .then(() => {
        setDeleteCommentButtonText('Delete Comment');
        setDeleteCommentSubmitting(false);
        closeModalHandler();
      })
      .catch((error) => {
        setModalJSX(
          <GenericErrorModal
            show={show}
            message={error.message}
          />,
        );
      });
  };

  const onConfirmDeleteAlert = () => {
    setDeleteAlertButtonText('Deleting Alert...');
    setDeleteAlertSubmitting(true);
    handleDeleteAlert();
  };

  const onConfirmDeleteAction = () => {
    setDeleteActionButtonText('Deleting Action...');
    setDeleteActionSubmitting(true);
    handleDeleteAlertAction(modalPayload)
      .then(() => {
        setDeleteActionButtonText('Delete Action');
        setDeleteActionSubmitting(false);
        dispatch(fetchSingleAlert({ alertId }));
        closeModalHandler();
      })
      .catch((error) => {
        setModalJSX(
          <GenericErrorModal
            show={show}
            message={error.message}
          />,
        );
      });
  };

  const onConfirmAcknowledgeAlert = () => {
    setAcknowledgeAlertButtonText('Acknowledging Alert...');
    setAcknowledgeAlertSubmitting(true);
    handleAcknowledgeAlert(acknowledgeText)
      .then(() => {
        setAcknowledgeAlertButtonText('Acknowledge Alert');
        setAcknowledgeAlertSubmitting(true);
        closeModalHandler();
      })
      .catch((error) => {
        setModalJSX(
          <GenericErrorModal
            show={show}
            message={error.message}
          />,
        );
      });
  };

  const modalMap = {};

  modalMap[alertDetailsModalTypes.DELETE_ACTION] = {
    props: {
      showConfirmCallToAction: true,
      confirmButtonText: deleteActionButtonText,
      confirmButtonAction: onConfirmDeleteAction,
      isBtnDisabled: deleteActionSubmitting,
    },
    title: 'Delete Alert Action',
    body: `Are you sure you want to delete alert action ${modalPayload}?`,
  };
  modalMap[alertDetailsModalTypes.DELETE_COMMENT] = {
    props: {
      showConfirmCallToAction: true,
      confirmButtonText: deleteCommentButtonText,
      confirmButtonAction: onConfirmDeleteComment,
      isBtnDisabled: deleteCommentSubmitting,
    },
    title: 'Delete Comment',
    body: 'Are you sure you want to permanently delete this comment?',
  };
  modalMap[alertDetailsModalTypes.DELETE_ALERT] = {
    props: {
      showConfirmCallToAction: true,
      confirmButtonText: deleteAlertButtonText,
      confirmButtonAction: onConfirmDeleteAlert,
      isBtnDisabled: deleteAlertSubmitting,
    },
    title: 'Delete Alert',
    body: `Are you sure you want to delete ${description} ?`,
  };
  modalMap[alertDetailsModalTypes.ACKNOWLEDGE_ALERT] = {
    props: {
      showConfirmCallToAction: true,
      confirmButtonText: acknowledgeAlertButtonText,
      confirmButtonAction: onConfirmAcknowledgeAlert,
      isBtnDisabled: acknowledgeAlertSubmitting,
    },
    title: 'Acknowledge Alert',
    body: (<AcknowledgeAlertModalContent
      alertDescription={description}
      handleTextChange={setAcknowledgeText}
    />),
  };

  return (
    <ModalComponent show={show} close={closeModalHandler} {...modalMap[type].props}>
      <Modal.Header closeButton>
        <Modal.Title>{modalMap[type].title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>{modalMap[type].body}</p>
      </Modal.Body>
    </ModalComponent>
  );
};

AlertDetailsModal.defaultProps = {
  handleDeleteAlertAction: null,
  handleDeleteAlertComment: null,
  handleAcknowledgeAlert: null,
  type: null,
  modalPayload: null,
};

AlertDetailsModal.propTypes = {
  setModalJSX: PropTypes.func.isRequired,
  handleDeleteAlertAction: PropTypes.func,
  handleDeleteAlert: PropTypes.func.isRequired,
  handleDeleteAlertComment: PropTypes.func,
  handleAcknowledgeAlert: PropTypes.func,
  type: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  modalPayload: PropTypes.any,
  description: PropTypes.string.isRequired,
  alertId: PropTypes.number.isRequired,
};

export { AlertDetailsModal };
export default connectModalContainerContextToComponent(AlertDetailsModal);
